import {fetchApi} from "@/utils/fetcher";
import { clearSession, setTokens } from "./Store/tokenstore";
import { refreshUserAffiliations } from "./Store/useraffiliationstore";

export type ServiceResult<TData = unknown> = {
  isSuccess: boolean;
  data?: TData | null;
  errorMessage?: string | null;
  statusCode: number;
};

type LoginData = {
  bearerToken: string;
  refreshToken: string;
  refreshTokenExpiresAtUtc: string;
};

// The API answers 200 with this envelope even when it failed, so the body - not the
// status - is what tells success from failure.
export async function readServiceResult<TData>(response: Response): Promise<ServiceResult<TData> | null> {
  try {
    const body = await response.json();
    return typeof body?.isSuccess === 'boolean' ? (body as ServiceResult<TData>) : null;
  } catch {
    return null;
  }
}

export type AuthResult = {
  success: boolean;
  reason?: 'invalid-credentials' | 'request-failed';
};


export async function authenticateWithCredentials(email: string, password: string): Promise<AuthResult> {
  let response: Response;
  try {
    response = await fetchApi('/api/authentication/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }, true);
  } catch {
    return { success: false, reason: 'request-failed' };
  }

  const result = await readServiceResult<LoginData>(response);
  if (!result) {
    return { success: false, reason: 'request-failed' };
  }

  if (!result.isSuccess || !result.data) {
    return { success: false, reason: 'invalid-credentials' };
  }

  /* Whoever used this browser last may never have logged out, and their data
     now survives the tab being closed - so it goes before the new tokens land,
     rather than being left for this login to inherit. */
  clearSession();
  setTokens(result.data.bearerToken, result.data.refreshToken, result.data.refreshTokenExpiresAtUtc);
  // Callers act on the new session the moment this resolves - and the member
  // gate reads affiliations synchronously - so warm the cache before returning.
  await refreshUserAffiliations();
  return { success: true };
}
